/**
 * QC Inline Actions (Feb 2026)
 *
 * Sticky action bar rendered inside the Fulfillment tab's QC Review Mode
 * banner so QC reviewers can Preview PDF / Approve / Return for Correction
 * without bouncing back to "My Queue".
 *
 * Mirrors the action handlers in QCDashboard.jsx but works against the
 * single order currently being reviewed.
 *
 * Endpoints used (all already implemented in backend/server.py):
 *   POST /api/orders/{id}/qc-assign      — claim
 *   POST /api/orders/{id}/qc-unassign    — release
 *   POST /api/orders/{id}/qc-approve     — approve
 *   POST /api/orders/{id}/qc-return      — return for correction (with severity)
 *   GET  /api/orders/{id}/tax-cert/preview-pdf — preview generated PDF
 *
 * QC-CONSOLIDATE-001 (Feb 2026): the hard "Reject" workflow has been
 * removed. Critical issues now flow through Return-for-Correction with
 * severity='critical'.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  RotateCcw,
  Eye,
  UserPlus,
  UserMinus,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { PdfPreviewViewer } from '../ui/PdfPreviewViewer';
import QCApproveModal from './QCApproveModal';
import QCFinalReviewStep from './QCFinalReviewStep'; // QC-FINAL-CHECKLIST-001
import QCReturnModal from './QCReturnModal';
import { qcActionPath, postQcReturn } from '../../lib/qcComponent';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const LIFT = 'transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out hover:-translate-y-px hover:shadow';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// QC-REVIEW-UI-COMPACT-002: `compact` renders the same buttons inline inside the QC command bar
// (no wrapper border / spacer). Handlers, guards and data-testids are identical in both modes.
const QCInlineActions = ({ orderData, currentUser, onOrderUpdate, showPreviewPdf = true, compact = false }) => {
  const navigate = useNavigate();

  // Refetch order after claim/release so the UI re-renders with the new
  // qc_assigned_to value. The backend exposes GET /api/orders/{id}/details
  // (returns { order, property, customer }) — there is no bare GET
  // /api/orders/{id}, so we use /details and shallow-merge `order`.
  //
  // IMPORTANT: MongoDB strips keys whose value is None, so an unassigned
  // order comes back WITHOUT `qc_assigned_to`. A naive `{...prev, ...updated}`
  // merge would then keep the stale qc_assigned_to from the previous render.
  // We explicitly null out the QC assignment fields so the merge clears them.
  const refreshOrder = useCallback(async () => {
    if (!orderData?.id || !onOrderUpdate) return;
    try {
      const res = await fetch(`${API}/orders/${orderData.id}/details`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const payload = await res.json();
        const updated = payload?.order || payload;
        const patch = {
          ...updated,
          qc_assigned_to: updated.qc_assigned_to ?? null,
          qc_assigned_at: updated.qc_assigned_at ?? null,
          qc_assigned_to_name: updated.qc_assigned_to_name ?? null,
        };
        onOrderUpdate(patch);
      }
    } catch {
      /* non-fatal — toast already reported the underlying action */
    }
  }, [orderData?.id, onOrderUpdate]);

  // Compute role flags
  const userId = currentUser?.id || null;
  const assignedTo = orderData?.qc_assigned_to || null;
  const submittedBy = orderData?.qc_submitted_by || null;

  const assignedToMe = assignedTo && assignedTo === userId;
  const unassigned = !assignedTo;
  const isSelfSubmission = submittedBy && submittedBy === userId;

  // Action loading flags
  const [claiming, setClaiming] = useState(false);
  const [releasing, setReleasing] = useState(false);

  // Modal open flags
  const [showApprove, setShowApprove] = useState(false);
  const [showQcChecklist, setShowQcChecklist] = useState(false); // QC-FINAL-CHECKLIST-001: step before the approve modal
  const [showReturn, setShowReturn] = useState(false);

  // Preview-PDF state
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfError, setPdfError] = useState('');

  const cleanupPdf = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setPdfData(null);
    setPdfError('');
  }, [pdfUrl]);

  // Fetch PDF when modal opens
  useEffect(() => {
    if (!showPdf) return;
    let cancelled = false;
    (async () => {
      setPdfLoading(true);
      setPdfError('');
      setPdfData(null);
      setPdfUrl('');
      try {
        const res = await fetch(
          `${API}/orders/${orderData.id}/tax-cert/preview-pdf`,
          { headers: authHeaders() }
        );
        if (cancelled) return;
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          setPdfError(text || 'Could not generate PDF preview.');
        } else {
          const buf = await res.arrayBuffer();
          if (cancelled) return;
          setPdfData(new Uint8Array(buf));
          const blob = new Blob([buf], { type: 'application/pdf' });
          setPdfUrl(URL.createObjectURL(blob));
        }
      } catch {
        if (!cancelled) setPdfError('Network error loading PDF preview.');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [showPdf, orderData?.id]);

  /* ── Claim / Release ─────────────────────────────────────── */
  const handleClaim = async () => {
    if (!orderData?.id) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API}/orders/${orderData.id}/qc-assign`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(`${orderData.order_number} assigned to you.`);
        await refreshOrder();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not assign order.');
      }
    } catch {
      toast.error('Network error.');
    } finally {
      setClaiming(false);
    }
  };

  const handleRelease = async () => {
    if (!orderData?.id) return;
    setReleasing(true);
    try {
      const res = await fetch(`${API}/orders/${orderData.id}/qc-unassign`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(`${orderData.order_number} released.`);
        await refreshOrder();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not release order.');
      }
    } catch {
      toast.error('Network error.');
    } finally {
      setReleasing(false);
    }
  };

  /* ── Approve / Return / Reject ───────────────────────────── */
  const approveConfirm = async (notes, extra = {}) => {
    try {
      const res = await fetch(`${API}${qcActionPath(orderData, 'approve')}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || null, ...extra }),
      });
      if (res.ok) {
        toast.success(`${orderData.order_number} approved.`);
        setTimeout(() => { setShowApprove(false); navigate('/qc-dashboard'); }, 900);
        return true;
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail?.message || d.detail || 'Approve failed.', { duration: d.detail?.error === 'TAX_NARRATIVE_MISSING' ? 12000 : 4000 });
      }
    } catch {
      toast.error('Network error.');
    }
    return false;
  };

  const returnConfirm = async (category, errorType, notes, evidenceFileIds, severity, affectedComponents = null) => {
    if (!category || !notes) return;
    try {
      const res = await postQcReturn(orderData, { category, errorType, notes, evidenceFileIds, severity, affectedComponents });
      if (res.ok) {
        toast.success(`${orderData.order_number} returned for correction.`);
        setShowReturn(false);
        navigate('/qc-dashboard');
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Return failed.');
      }
    } catch {
      toast.error('Network error.');
    }
  };

  // QC-CONSOLIDATE-001 (Feb 2026): rejectConfirm removed (workflow consolidated).

  /* ── Guards for action buttons ───────────────────────────── */
  const tryApprove = () => {
    if (isSelfSubmission) {
      toast.error('Separation of duties: you cannot approve your own submission.');
      return;
    }
    if (!assignedToMe) {
      toast.error('Claim this order first before approving.');
      return;
    }
    // QC-FINAL-CHECKLIST-001: mandatory QC checklist first (step self-skips for Tax/MLS/portal
    // or when a valid acknowledgment already exists), then the SAME approve modal + handler.
    setShowQcChecklist(true);
  };

  const tryReturn = () => {
    if (!assignedToMe) {
      toast.error('Claim this order first before returning for correction.');
      return;
    }
    setShowReturn(true);
  };

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <>
      <div
        className={compact
          ? 'flex flex-wrap items-center gap-2'
          : 'ml-8 mt-1 flex flex-wrap items-center gap-2 pt-3 border-t border-green-200'}
        data-testid="qc-inline-action-bar"
      >
        {/* Preview PDF — Tax Cert QC only (hidden for Title Search QC Review) */}
        {showPreviewPdf && (
          <Button
            variant="outline"
            size="sm"
            className={`${LIFT} ${compact ? 'h-9 border-slate-200 text-slate-700 hover:bg-slate-50' : 'h-8 border-green-300 text-green-800 hover:bg-green-100'}`}
            onClick={() => setShowPdf(true)}
            data-testid="qc-inline-preview-pdf-btn"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Preview PDF
          </Button>
        )}

        {/* Claim / Release */}
        {unassigned && (
          <Button
            variant="outline"
            size="sm"
            className={`h-9 ${LIFT} border-blue-300 text-blue-700 hover:bg-blue-50`}
            onClick={handleClaim}
            disabled={claiming}
            data-testid="qc-inline-claim-btn"
          >
            {claiming ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4 mr-1.5" />
            )}
            Claim & Review
          </Button>
        )}
        {assignedToMe && (
          <Button
            variant="outline"
            size="sm"
            className={`h-9 ${LIFT} border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900`}
            onClick={handleRelease}
            disabled={releasing}
            title="Release this order back to the unassigned queue"
            data-testid="qc-inline-release-btn"
          >
            {releasing ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <UserMinus className="w-4 h-4 mr-1.5" />
            )}
            Release
          </Button>
        )}

        {/* Spacer pushes decision buttons to the right (legacy layout only) */}
        {!compact && <div className="flex-1" />}

        {/* Approve */}
        <Button
          size="sm"
          className={`h-9 ${LIFT} bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm shadow-emerald-600/30 disabled:opacity-50`}
          onClick={tryApprove}
          disabled={!assignedToMe || isSelfSubmission}
          title={
            isSelfSubmission
              ? 'You cannot approve your own submission'
              : !assignedToMe
                ? 'Claim this order before approving'
                : 'Approve & complete this order'
          }
          data-testid="qc-inline-approve-btn"
        >
          <CheckCircle className="w-4 h-4 mr-1.5" />
          Approve
        </Button>

        {/* Return for Correction */}
        <Button
          variant="outline"
          size="sm"
          className={`h-9 ${LIFT} border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 font-medium disabled:opacity-50`}
          onClick={tryReturn}
          disabled={!assignedToMe}
          title={
            !assignedToMe
              ? 'Claim this order before returning for correction'
              : 'Return to processor for correction'
          }
          data-testid="qc-inline-return-btn"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" />
          Return for Correction
        </Button>

        {/* Reject */}
        {/* QC-CONSOLIDATE-001 (Feb 2026): Reject button removed.
            Critical issues now flow through Return for Correction
            with severity='critical'. */}
      </div>

      {/* ── PDF Preview Dialog ────────────────────────────────── */}
      {showPreviewPdf && (
      <Dialog
        open={showPdf}
        onOpenChange={(o) => {
          setShowPdf(o);
          if (!o) cleanupPdf();
        }}
      >
        <DialogContent
          className="sm:max-w-4xl max-h-[90vh] flex flex-col"
          data-testid="qc-inline-preview-pdf-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Eye className="h-5 w-5 text-green-600" />
              Tax Certificate Preview — {orderData?.order_number}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Read-only preview of the generated certificate that will be sent
              upon approval. Use the action buttons in the QC Review banner to
              approve, return, or reject.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col gap-4 mt-2">
            {pdfLoading && (
              <div
                className="flex-1 flex items-center justify-center py-16"
                data-testid="qc-inline-preview-pdf-loading"
              >
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
                  <p className="text-sm font-medium text-slate-600">
                    Generating PDF preview...
                  </p>
                </div>
              </div>
            )}

            {!pdfLoading && (pdfData || pdfUrl) && (
              <PdfPreviewViewer
                pdfData={pdfData}
                fallbackUrl={pdfUrl}
                className="flex-1 min-h-[500px]"
              />
            )}

            {!pdfLoading && !pdfData && !pdfUrl && (
              <div
                className="flex-1 flex items-center justify-center py-16 border border-dashed border-slate-200 rounded-lg bg-slate-50"
                data-testid="qc-inline-preview-pdf-error"
              >
                <div className="text-center max-w-md">
                  <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">
                    PDF preview could not be loaded.
                  </p>
                  {pdfError && (
                    <p className="text-xs text-slate-500 mt-1 break-words">
                      {pdfError}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}

      {/* ── Approve / Return / Reject modals ─────────────────── */}
      <QCFinalReviewStep
        open={showQcChecklist}
        order={orderData}
        onCancel={() => setShowQcChecklist(false)}
        onReady={() => { setShowQcChecklist(false); setShowApprove(true); }}
      />
      <QCApproveModal
        isOpen={showApprove}
        onClose={() => setShowApprove(false)}
        onConfirm={approveConfirm}
        order={orderData}
      />
      <QCReturnModal
        isOpen={showReturn}
        onClose={() => setShowReturn(false)}
        onConfirm={returnConfirm}
        order={orderData}
      />
    </>
  );
};

export default QCInlineActions;
